import React from 'react';
import { Skeleton } from './ui/skeleton';

/**
 * Skeleton components for various UI elements
 * Provides smooth loading states for better perceived performance
 */

// Content Card Skeleton - for movie/content cards in browse page
export const ContentCardSkeleton = ({ size = 'normal' }) => {
  const width = size === 'large' ? 'w-64' : 'w-44';
  const height = size === 'large' ? 'h-96' : 'h-64';
  
  return (
    <div className={`${width} flex-shrink-0`}>
      <Skeleton className={`${width} ${height} rounded-lg bg-gray-800`} />
      <div className="mt-2 space-y-2">
        <Skeleton className="h-4 w-3/4 bg-gray-800" />
        <Skeleton className="h-3 w-1/2 bg-gray-800" />
      </div>
    </div>
  );
};

// Content Row Skeleton - for horizontal scrolling rows
export const ContentRowSkeleton = ({ title = true, count = 6, size = 'normal' }) => (
  <div className="py-4">
    {title && <Skeleton className="h-6 w-48 mb-4 bg-gray-800" />}
    <div className="flex gap-4 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <ContentCardSkeleton key={`content-skeleton-${i}`} size={size} />
      ))}
    </div>
  </div>
);

// Hero Section Skeleton - for the main hero banner
export const HeroSkeleton = () => (
  <div className="relative w-full h-[70vh] md:h-[80vh]">
    <Skeleton className="absolute inset-0 bg-gray-800" />
    <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
      <Skeleton className="h-12 w-64 mb-4 bg-gray-700" />
      <Skeleton className="h-4 w-96 max-w-full mb-2 bg-gray-700" />
      <Skeleton className="h-4 w-80 max-w-full mb-6 bg-gray-700" />
      <div className="flex gap-3">
        <Skeleton className="h-12 w-32 rounded-lg bg-gray-700" />
        <Skeleton className="h-12 w-40 rounded-lg bg-gray-700" />
      </div>
    </div>
  </div>
);

// User Card Skeleton - for user profile cards
export const UserCardSkeleton = () => (
  <div className="flex items-center gap-4 p-4 bg-gray-800/50 rounded-lg">
    <Skeleton className="w-12 h-12 rounded-full bg-gray-700" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-32 bg-gray-700" />
      <Skeleton className="h-3 w-24 bg-gray-700" />
    </div>
    <Skeleton className="h-8 w-20 rounded-lg bg-gray-700" />
  </div>
);

// Stats Card Skeleton - for analytics/stats cards
export const StatsCardSkeleton = () => (
  <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
    <Skeleton className="h-4 w-24 mb-3 bg-gray-700" />
    <Skeleton className="h-8 w-20 mb-2 bg-gray-700" />
    <Skeleton className="h-3 w-32 bg-gray-700" />
  </div>
);

// Table Row Skeleton - for data tables
export const TableRowSkeleton = ({ columns = 5 }) => (
  <div className="flex items-center gap-4 p-4 border-b border-gray-800">
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton 
        key={`table-col-${i}`} 
        className={`h-4 bg-gray-700 ${i === 0 ? 'w-8' : i === 1 ? 'w-32' : 'flex-1'}`} 
      />
    ))}
  </div>
);

// Settings Panel Skeleton - for settings sections
export const SettingsPanelSkeleton = () => (
  <div className="space-y-4 p-6 bg-gray-800/50 rounded-lg border border-gray-700">
    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="w-10 h-10 rounded-lg bg-gray-700" />
      <div>
        <Skeleton className="h-5 w-40 mb-1 bg-gray-700" />
        <Skeleton className="h-3 w-56 bg-gray-700" />
      </div>
    </div>
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`settings-row-${i}`} className="flex items-center justify-between">
          <Skeleton className="h-4 w-32 bg-gray-700" />
          <Skeleton className="h-6 w-12 rounded-full bg-gray-700" />
        </div>
      ))}
    </div>
  </div>
);

// Message Skeleton - for chat messages
export const MessageSkeleton = ({ isOwn = false }) => (
  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
    <div className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
      <Skeleton className="w-10 h-10 rounded-full bg-gray-700 flex-shrink-0" />
      <div className={`space-y-2 ${isOwn ? 'items-end' : ''}`}>
        <Skeleton className={`h-16 ${isOwn ? 'w-48' : 'w-64'} rounded-lg bg-gray-700`} />
        <Skeleton className="h-3 w-16 bg-gray-700" />
      </div>
    </div>
  </div>
);

// Chat List Skeleton - for conversation list
export const ChatListSkeleton = ({ count = 5 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={`chat-skeleton-${i}`} className="flex items-center gap-3 p-3 rounded-lg">
        <Skeleton className="w-12 h-12 rounded-full bg-gray-700" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-28 bg-gray-700" />
          <Skeleton className="h-3 w-48 bg-gray-700" />
        </div>
        <Skeleton className="w-5 h-5 rounded-full bg-gray-700" />
      </div>
    ))}
  </div>
);

// Notification Skeleton
export const NotificationSkeleton = () => (
  <div className="flex items-start gap-3 p-4 bg-gray-800/50 rounded-lg">
    <Skeleton className="w-10 h-10 rounded-full bg-gray-700" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-3/4 bg-gray-700" />
      <Skeleton className="h-3 w-1/2 bg-gray-700" />
    </div>
    <Skeleton className="w-3 h-3 rounded-full bg-gray-700" />
  </div>
);

// Full Page Skeleton - for entire page loading
export const PageSkeleton = () => (
  <div className="min-h-screen bg-black p-4 md:p-8">
    {/* Header Skeleton */}
    <div className="flex items-center justify-between mb-8">
      <Skeleton className="h-8 w-32 bg-gray-800" />
      <div className="flex gap-4">
        <Skeleton className="w-10 h-10 rounded-full bg-gray-800" />
        <Skeleton className="w-10 h-10 rounded-full bg-gray-800" />
        <Skeleton className="w-10 h-10 rounded-full bg-gray-800" />
      </div>
    </div>
    
    {/* Hero Skeleton */}
    <HeroSkeleton />
    
    {/* Content Rows */}
    <div className="mt-8 space-y-8">
      <ContentRowSkeleton />
      <ContentRowSkeleton size="large" />
      <ContentRowSkeleton />
    </div>
  </div>
);

// Analytics Dashboard Skeleton
export const AnalyticsSkeleton = () => (
  <div className="space-y-6">
    {/* Stats Row */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatsCardSkeleton key={`stats-${i}`} />
      ))}
    </div>
    
    {/* Chart Skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
        <Skeleton className="h-5 w-32 mb-4 bg-gray-700" />
        <Skeleton className="h-48 w-full bg-gray-700 rounded-lg" />
      </div>
      <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
        <Skeleton className="h-5 w-32 mb-4 bg-gray-700" />
        <Skeleton className="h-48 w-full bg-gray-700 rounded-lg" />
      </div>
    </div>
    
    {/* Table Skeleton */}
    <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
      <Skeleton className="h-5 w-40 mb-4 bg-gray-700" />
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRowSkeleton key={`table-row-${i}`} />
      ))}
    </div>
  </div>
);

// Profile Page Skeleton
export const ProfileSkeleton = () => (
  <div className="max-w-4xl mx-auto p-6 space-y-6">
    {/* Profile Header */}
    <div className="flex flex-col md:flex-row items-center gap-6 p-6 bg-gray-800/50 rounded-lg">
      <Skeleton className="w-32 h-32 rounded-full bg-gray-700" />
      <div className="flex-1 text-center md:text-left space-y-3">
        <Skeleton className="h-8 w-48 mx-auto md:mx-0 bg-gray-700" />
        <Skeleton className="h-4 w-32 mx-auto md:mx-0 bg-gray-700" />
        <Skeleton className="h-4 w-64 mx-auto md:mx-0 bg-gray-700" />
      </div>
      <Skeleton className="h-10 w-28 rounded-lg bg-gray-700" />
    </div>
    
    {/* Stats */}
    <div className="grid grid-cols-3 gap-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={`profile-stat-${i}`} className="p-4 bg-gray-800/50 rounded-lg text-center">
          <Skeleton className="h-8 w-12 mx-auto mb-2 bg-gray-700" />
          <Skeleton className="h-4 w-20 mx-auto bg-gray-700" />
        </div>
      ))}
    </div>
    
    {/* Content Grid */}
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={`profile-content-${i}`} className="aspect-square rounded-lg bg-gray-700" />
      ))}
    </div>
  </div>
);

export default {
  ContentCardSkeleton,
  ContentRowSkeleton,
  HeroSkeleton,
  UserCardSkeleton,
  StatsCardSkeleton,
  TableRowSkeleton,
  SettingsPanelSkeleton,
  MessageSkeleton,
  ChatListSkeleton,
  NotificationSkeleton,
  PageSkeleton,
  AnalyticsSkeleton,
  ProfileSkeleton
};
